/**
 * /tour — Public product tour page for SiteOrbit.
 *
 * Structure:
 *   1. Hero with headline + CTAs
 *   2. 8 annotated-screenshot feature sections (alternating layout)
 *   3. "Built for site, works without signal" mobile section
 *   4. Linkback to pricing + FAQs (live on landing page)
 *   5. Final CTA band
 *
 * Image placeholders:
 *   Drop real screenshots into /app/frontend/public/tour/<slug>.png
 *   and they will be picked up automatically by <TourImage slug="..." />.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';

// ============================================================
// CONFIG
// ============================================================

const FEATURE_SECTIONS = [
  {
    slug: 'sites-dashboard',
    eyebrow: 'Every job, every site',
    heading: 'Sites at a glance',
    body: "See every active job on one screen. Filter by status, client, or date. Drill into any site to see who's there now, what's been done, and what's outstanding.",
    callouts: [
      'Live status indicators for every active site',
      'Smart assignment — drag-and-drop workers onto jobs',
      'Filters by client, status, postcode, or date range',
    ],
  },
  {
    slug: 'team-capacity',
    eyebrow: 'Your team, in one view',
    heading: "See who's free, who's stretched, and who's where",
    body: 'Team Capacity shows you exactly which workers are available this week, who has overtime stacked up, and which sites need cover. Spot bottlenecks before they happen.',
    callouts: [
      'Real-time view of every team member across every site',
      'Holiday and overtime baked into the capacity calculation',
      'Click any worker to see their full schedule and current task',
    ],
  },
  {
    slug: 'gps-clockin',
    eyebrow: 'Time tracked from site, automatically',
    heading: 'GPS clock-in with selfie verification',
    body: "Workers clock in from the mobile app — geofenced to the actual site, with a selfie for proof. No more guesswork on hours, no more 'I forgot to clock out' on a Friday.",
    callouts: [
      'Geofenced clock-in — only works inside the assigned site radius',
      'Selfie attached to every shift for verification',
      'Automatic timesheets feed into the pay run with zero data entry',
    ],
  },
  {
    slug: 'quoting',
    eyebrow: 'From enquiry to payment',
    heading: 'Quote, invoice, and get paid',
    body: 'Build branded quotes in minutes. Convert to invoice with one click. Track who has paid and who needs a nudge. CIS-aware for UK contractors.',
    callouts: [
      'AI-assisted quote line items — describe the job, get a draft',
      'One-click conversion: quote → invoice → payment status',
      'CIS deductions calculated automatically for UK construction',
    ],
  },
  {
    slug: 'neo-ai',
    eyebrow: 'Your AI second-in-command',
    heading: 'Neo AI does the busy-work',
    body: "SiteOrbit's built-in AI drafts quotes, writes site reports from your notes, summarises a week's progress for a client update, and flags overdue tasks before they slip.",
    callouts: [
      'Draft quote items from a few lines of plain English',
      'Turn site notes into polished progress reports',
      'Smart task prioritisation based on deadlines and team capacity',
    ],
  },
  {
    slug: 'photo-gallery',
    eyebrow: 'A picture record of every job',
    heading: 'Photos and notes from every visit',
    body: 'Every photo your workers take is tagged with site, date, and worker. Search by site or date range. Share a tidy photo report with your client in two clicks.',
    callouts: [
      'Auto-tagged by site, worker, and timestamp',
      'Filter, search, and bulk-download photos for any job',
      'Generate client-ready photo reports without any manual sorting',
    ],
  },
  {
    slug: 'approvals',
    eyebrow: 'No more WhatsApp chaos',
    heading: 'Approvals without the back-and-forth',
    body: 'Materials, expenses, holidays, and overtime — one place for the request, one place for the approval. Workers know what to ask for, admins know what to action.',
    callouts: [
      'Centralised approval queues for materials, expenses, holidays, overtime',
      'Push notifications to admins when a request lands',
      'Full audit trail of who approved what and when',
    ],
  },
  {
    slug: 'payments',
    eyebrow: 'Pay days that take minutes',
    heading: 'Run payroll without the spreadsheet wrestling',
    body: 'Timesheets, overtime, approved expenses, and CIS deductions all flow into a single pay run. Review, approve, and produce CIS statements without leaving the app.',
    callouts: [
      'Pay runs auto-built from hours, overtime, and approved expenses',
      'Lock in hourly rates per pay period for accurate retroactive runs',
      'CIS statements generated for every UK subcontractor',
    ],
  },
];


// ============================================================
// Sub-components
// ============================================================

/**
 * Screenshot placeholder that auto-detects whether a real image exists.
 * Drop a PNG at /tour/<slug>.png and it'll be used; otherwise a clean
 * placeholder card is shown. When a real image is present, clicking it
 * opens the lightbox.
 */
const TourImage = ({ slug, alt, ext = 'png', coverFit = false, onOpen }) => {
  const [errored, setErrored] = useState(false);

  if (errored) {
    return (
      <div
        className="w-full aspect-[4/3] rounded-xl bg-gradient-to-br from-slate-50 to-slate-100 border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 p-8"
        data-testid={`tour-image-placeholder-${slug}`}
      >
        <svg className="w-12 h-12 mb-3 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <p className="text-sm font-medium text-slate-500 mb-1">Screenshot placeholder</p>
        <code className="text-xs text-slate-400 bg-slate-200/50 px-2 py-1 rounded">/tour/{slug}.{ext}</code>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onOpen?.(slug, alt)}
      className={`group relative w-full block rounded-xl overflow-hidden border border-slate-200 shadow-xl hover:shadow-2xl transition-shadow focus:outline-none focus:ring-4 focus:ring-purple-500/30 ${coverFit ? 'aspect-[4/3] bg-slate-900' : ''}`}
      aria-label={`View enlarged screenshot: ${alt}`}
      data-testid={`tour-image-btn-${slug}`}
    >
      <img
        src={`/tour/${slug}.${ext}`}
        alt={alt}
        className={
          coverFit
            ? 'absolute inset-0 w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-[1.02]'
            : 'w-full transition-transform duration-500 group-hover:scale-[1.02]'
        }
        loading="lazy"
        onError={() => setErrored(true)}
        data-testid={`tour-image-${slug}`}
      />
      <span
        className="absolute top-3 right-3 px-2.5 py-1.5 rounded-full bg-black/60 backdrop-blur-sm text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1.5 pointer-events-none"
        aria-hidden="true"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m-3-3h6" />
        </svg>
        Click to enlarge
      </span>
    </button>
  );
};

/**
 * Full-screen lightbox for tour images. Supports:
 *   - Click outside / × button / Esc key to close
 *   - Left/Right arrow keys to cycle through all images
 *   - Body scroll lock while open
 */
const Lightbox = ({ slug, alt, ext = 'png', slugs, onClose, onNav }) => {
  const handleKey = useCallback((e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft') onNav(-1);
    if (e.key === 'ArrowRight') onNav(1);
  }, [onClose, onNav]);

  useEffect(() => {
    if (!slug) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', handleKey);
    };
  }, [slug, handleKey]);

  if (!slug) return null;

  const currentIndex = slugs.indexOf(slug);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < slugs.length - 1;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 animate-in fade-in duration-200"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Enlarged screenshot: ${alt}`}
      data-testid="tour-lightbox"
    >
      {/* Close */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10"
        aria-label="Close enlarged view"
        data-testid="tour-lightbox-close"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Prev arrow */}
      {hasPrev && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onNav(-1); }}
          className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10"
          aria-label="Previous screenshot"
          data-testid="tour-lightbox-prev"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {/* Next arrow */}
      {hasNext && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onNav(1); }}
          className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10"
          aria-label="Next screenshot"
          data-testid="tour-lightbox-next"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* Image — stop propagation so clicks on it don't close */}
      <div
        className="relative max-w-[95vw] max-h-[88vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={`/tour/${slug}.${ext}`}
          alt={alt}
          className="max-w-full max-h-[82vh] object-contain rounded-lg shadow-2xl"
          data-testid="tour-lightbox-image"
        />
        <div className="mt-4 px-4 py-1.5 bg-black/40 rounded-full text-white text-sm font-medium">
          {alt}
          {slugs.length > 1 && (
            <span className="ml-2 text-white/60 text-xs">
              {currentIndex + 1} / {slugs.length}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const FeatureSection = ({ section, index, onImageOpen }) => {
  const flipped = index % 2 === 1;
  return (
    <section
      className="py-16 sm:py-20 lg:py-24"
      data-testid={`tour-section-${section.slug}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${flipped ? 'lg:[&>*:first-child]:order-2' : ''}`}>
          <div>
            <p className="text-xs uppercase tracking-widest text-purple-600 font-semibold mb-3">
              {section.eyebrow}
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 leading-tight">
              {section.heading}
            </h2>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-6">
              {section.body}
            </p>
            <ul className="space-y-3">
              {section.callouts.map((c, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mt-0.5">
                    <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <span className="text-sm sm:text-base text-gray-700">{c}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <TourImage
              slug={section.slug}
              alt={section.heading}
              ext={section.ext}
              coverFit={section.coverFit}
              onOpen={onImageOpen}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================================
// Main page
// ============================================================

const TourPage = () => {
  const navigate = useNavigate();

  // All image slugs (including the mobile-app one in the offline band) make up
  // the lightbox's left/right keyboard-nav set.
  const allSlugs = [...FEATURE_SECTIONS.map(s => s.slug), 'mobile-app'];
  const altByslug = Object.fromEntries([
    ...FEATURE_SECTIONS.map(s => [s.slug, s.heading]),
    ['mobile-app', 'SiteOrbit mobile app'],
  ]);
  const extBySlug = Object.fromEntries([
    ...FEATURE_SECTIONS.map(s => [s.slug, s.ext || 'png']),
    ['mobile-app', 'jpg'],
  ]);

  const [lightbox, setLightbox] = useState({ slug: null, alt: '' });
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const openLightbox = (slug, alt) => setLightbox({ slug, alt });
  const closeLightbox = () => setLightbox({ slug: null, alt: '' });
  const navLightbox = (delta) => {
    setLightbox((prev) => {
      const idx = allSlugs.indexOf(prev.slug);
      const next = idx + delta;
      if (next < 0 || next >= allSlugs.length) return prev;
      const newSlug = allSlugs[next];
      return { slug: newSlug, alt: altByslug[newSlug] };
    });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="tour-page">
      {/* Top bar — matches landing page (dark at top, white on scroll) */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-lg' : 'bg-gray-900'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link
              to="/"
              className={`text-2xl font-bold transition-colors ${isScrolled ? 'text-gray-900' : 'text-white'}`}
              data-testid="tour-home-link"
            >
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 text-transparent bg-clip-text">Site</span>
              <span>Orbit</span>
            </Link>
            <div className="flex items-center gap-2 sm:gap-4">
              <button
                onClick={() => navigate('/admin/login')}
                className={`text-sm font-medium transition-colors hidden sm:block ${
                  isScrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white hover:text-blue-300'
                }`}
                data-testid="tour-login-btn"
              >
                Login
              </button>
              <button
                onClick={() => navigate('/register')}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-semibold rounded-full hover:shadow-lg transition-all"
                data-testid="tour-trial-btn-nav"
              >
                Start free trial
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-gradient-to-b from-slate-50 to-white pt-28 sm:pt-32 pb-16 sm:pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-widest text-purple-600 font-semibold mb-4">
            Take the tour
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-5 leading-tight">
            See SiteOrbit in <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">action</span>
          </h1>
          <p className="text-base sm:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            One app for time, tasks, quotes, photos, reporting and payments — built for construction and trade businesses.
            Take a quick look at how it works.
          </p>
        </div>
      </section>

      {/* Feature sections (alternating layout) */}
      <div className="bg-white">
        {FEATURE_SECTIONS.map((section, i) => (
          <div key={section.slug} className={i % 2 === 1 ? 'bg-slate-50' : 'bg-white'}>
            <FeatureSection section={section} index={i} onImageOpen={openLightbox} />
          </div>
        ))}
      </div>

      {/* Mobile / offline band */}
      <section className="relative py-20 sm:py-24 bg-gradient-to-br from-slate-900 via-slate-900 to-purple-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/4 left-10 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl"></div>
          <div className="absolute bottom-1/4 right-10 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs uppercase tracking-widest text-purple-300 font-semibold mb-3">Built for site</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-5 leading-tight">
                Works without signal. Made for muddy boots.
              </h2>
              <p className="text-base sm:text-lg text-slate-300 leading-relaxed mb-6">
                The mobile app is offline-first. Workers clock in, log photos, and update tasks even when there's no signal.
                Everything syncs the moment they're back online — no lost data, no shouted reminders.
              </p>
              <ul className="space-y-3">
                {[
                  'Offline mode for clock-in, tasks, photos, and notes',
                  'Push notifications for new tasks, approvals, and messages',
                  'Free mobile app for every worker — iOS and Android',
                ].map((c, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-white/10 flex items-center justify-center mt-0.5">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    <span className="text-sm sm:text-base text-slate-200">{c}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative max-w-xl mx-auto lg:mx-0 lg:ml-auto">
              <TourImage slug="mobile-app" alt="SiteOrbit mobile app" ext="jpg" onOpen={openLightbox} />
            </div>
          </div>
        </div>
      </section>

      {/* Compact linkback band — pricing & FAQs live on landing/faqs pages */}
      <section className="py-16 sm:py-20 bg-white border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Want to dig deeper?</h2>
          <p className="text-base text-gray-600 mb-7">
            Pricing, full FAQs, and everything else lives on the home page.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link
              to="/#pricing"
              className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-full hover:bg-gray-50 transition-all text-sm"
              data-testid="tour-link-pricing"
            >
              See pricing
            </Link>
            <Link
              to="/faqs"
              className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-full hover:bg-gray-50 transition-all text-sm"
              data-testid="tour-link-faqs"
            >
              Read the FAQs
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 sm:py-24 bg-gradient-to-r from-blue-600 to-purple-700">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
            Try SiteOrbit for 14 days
          </h2>
          <p className="text-base sm:text-lg text-blue-100 mb-8 max-w-xl mx-auto">
            No card, no commitment. Set up in a couple of minutes. See if it saves your team time — if not, walk away.
          </p>
          <button
            onClick={() => navigate('/register')}
            className="px-8 py-4 bg-white text-purple-700 font-bold rounded-full hover:shadow-2xl hover:scale-105 transition-all text-base sm:text-lg"
            data-testid="tour-trial-btn-footer"
          >
            Start your free trial
          </button>
          <p className="text-xs text-blue-200 mt-4">No card required · Cancel anytime · UK-based support</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 text-center text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <span className="bg-gradient-to-r from-blue-400 to-purple-400 text-transparent bg-clip-text font-bold">Site</span>
            <span className="text-white font-bold">Orbit</span>
            <span className="ml-3 text-gray-500">© {new Date().getFullYear()}</span>
          </div>
          <div className="flex gap-5">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <Link to="/faqs" className="hover:text-white transition-colors">FAQs</Link>
            <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms-of-service" className="hover:text-white transition-colors">Terms</Link>
          </div>
        </div>
      </footer>

      {/* Lightbox overlay — only rendered when a screenshot has been clicked */}
      <Lightbox
        slug={lightbox.slug}
        alt={lightbox.alt}
        ext={lightbox.slug ? (extBySlug[lightbox.slug] || 'png') : 'png'}
        slugs={allSlugs}
        onClose={closeLightbox}
        onNav={navLightbox}
      />
    </div>
  );
};

export default TourPage;
